import { TableCell, TableRow } from '@mui/material';
import { FC, Fragment, Reducer, useCallback, useEffect, useMemo, useReducer } from 'react';
import { useSearchParams } from 'react-router-dom';

// components
import ActionMenuDropdown from 'components/common/ActionMenuDropdown';
import { Alert } from 'components/common/Alert';
import NoDataFound from 'components/common/NoDataFound';
import TableComponent from 'components/common/TableComponent';
import TableContainer from 'components/common/TableContainer';
import TableLoader from 'components/common/TableLoader';
import TableSearchField from 'components/common/TableSearchField';
import TableTabsComponent from 'components/common/TableTabsComponent';

// constants, reducers, graphql, styles
import { UserTabType } from 'interfaces/TabTypes';
import { formatPhone, formatTimeStamp } from 'lib/helper';
import { Action, ActionType, State, initialState, userReducer } from 'reducer/userReducer';
import { textWhiteSpace } from 'styles/commonComponentStyle';
import {
  HTTP_STATUS,
  LOADING_TABLE_ROWS,
  TAB_QUERY_TYPE,
  USER_TABLE_HEADER,
  USER_TABS,
  VIEW_USER_ROUTE,
} from 'constants/index';
import {
  UserStatus,
  UsersPayload,
  useFindAllAdminLazyQuery,
  useUpdateUserStatusMutation,
} from 'generated/graphql';

const UsersTable: FC = () => {
  const [state, dispatch] = useReducer<Reducer<State, Action>>(userReducer, initialState);
  const { page, rowsPerPage, search, count, data, activeTab } = state;

  const [searchParams] = useSearchParams();
  const tab = searchParams.get(TAB_QUERY_TYPE);

  const [findAllAdmins, { loading: findAllAdminsLoading, error }] = useFindAllAdminLazyQuery({
    onCompleted: (data) => {
      const { findAllAdmin } = data;
      const { pagination, response, data: usersData } = findAllAdmin || {};
      const { status } = response || {};
      if (status === HTTP_STATUS.SUCCESS) {
        const { page, totalCount } = pagination || {};
        dispatch({ type: ActionType.SET_PAGE, page: page || 1 });
        dispatch({ type: ActionType.SET_COUNT, count: totalCount || 0 });
        dispatch({ type: ActionType.SET_DATA, data: usersData as UsersPayload['data'] });
      } else {
        resetPage();
      }
    },

    onError: () => {
      resetPage();
    },
  });

  const [updateUserStatus, { loading: updateUserStatusLoading }] = useUpdateUserStatusMutation({
    onCompleted: (data) => {
      const { updateUserStatus } = data;
      const { response } = updateUserStatus || {};
      const { message, status } = response || {};
      if (status === HTTP_STATUS.SUCCESS) {
        Alert.success(message || '');
        fetchAdmins();
      } else {
        Alert.error(message ?? '');
      }
    },

    onError: ({ message }) => {
      Alert.error(message);
    },
  });

  const resetPage = () => {
    dispatch({ type: ActionType.SET_PAGE, page: 1 });
    dispatch({ type: ActionType.SET_COUNT, count: 0 });
    dispatch({ type: ActionType.SET_DATA, data: [] });
  };

  const fetchAdmins = useCallback(async () => {
    await findAllAdmins({
      variables: {
        findAllAdminInput: {
          paginationOptions: {
            limit: rowsPerPage,
            page,
          },
          search,
          ...(activeTab !== 'all' && { status: activeTab }),
        },
      },
    });
  }, [findAllAdmins, search, rowsPerPage, page, activeTab]);

  useEffect(() => {
    (!search.length || search.length > 2) && fetchAdmins();
  }, [fetchAdmins, search.length]);

  const tabHandler = (_: React.SyntheticEvent<Element, Event>, value: string) => {
    dispatch({ type: ActionType.SET_ACTIVE_TAB, activeTab: value as UserTabType });
  };

  const onActiveHandler = async (id: string, status: boolean) => {
    await updateUserStatus({
      variables: {
        updateUserStatusInput: {
          id,
          status: status ? UserStatus.Inactive : UserStatus.Active,
        },
      },
    });
  };

  const onSearch = (val: string) => {
    dispatch({ type: ActionType.SET_PAGE, page: 1 });
    dispatch({ type: ActionType.SET_SEARCH, search: val });
  };

  const onClear = () => {
    dispatch({ type: ActionType.SET_PAGE, page: 1 });
    dispatch({ type: ActionType.SET_SEARCH, search: '' });
  };

  useMemo(() => {
    if (tab === 'all') dispatch({ type: ActionType.SET_ACTIVE_TAB, activeTab: 'all' });
  }, [tab]);

  const loading = findAllAdminsLoading || updateUserStatusLoading;

  const noData = Boolean((!findAllAdminsLoading && data?.length === 0) || error);

  return (
    <TableContainer>
      <TableTabsComponent activeTab={activeTab} tabsList={USER_TABS} tabHandler={tabHandler} />

      <TableSearchField search={search} onChange={onSearch} onClear={onClear} />

      <TableComponent
        noData={noData}
        page={page - 1}
        count={count}
        rowsPerPage={rowsPerPage}
        tableHeader={USER_TABLE_HEADER}
        setPage={(p: number) => dispatch({ type: ActionType.SET_PAGE, page: p + 1 })}
        setRowsPerPage={(r: number) => dispatch({ type: ActionType.SET_ROWS_PER_PAGE, rowsPerPage: r })}>
        {loading ? (
          <TableLoader columns={9} rows={LOADING_TABLE_ROWS} />
        ) : (
          <Fragment>
            {data?.map((cell) => {
              const {
                id,
                email,
                organization,
                firstName,
                lastName,
                phone,
                createdAt,
                status,
                organizationId,
              } = cell || {};
              const { name, maxUser, activeStaff, totalStaff } = organization || {};

              return (
                <TableRow key={id}>
                  <TableCell>
                    {firstName ?? ''} {lastName ?? '--'}
                  </TableCell>
                  <TableCell sx={textWhiteSpace}>{name ?? '--'}</TableCell>
                  <TableCell>{email ?? '--'}</TableCell>
                  <TableCell>{formatPhone(phone || '')}</TableCell>
                  <TableCell>{maxUser ?? 0}</TableCell>
                  <TableCell>{totalStaff ? totalStaff : 0}</TableCell>
                  <TableCell>{activeStaff ? activeStaff : 0}</TableCell>

                  <TableCell>{formatTimeStamp(createdAt, 'MM/DD/YY')}</TableCell>
                  <TableCell>
                    <ActionMenuDropdown
                      id={organizationId ?? ''}
                      secondaryId={id ?? ''}
                      isActive={status === UserStatus.Active}
                      viewRoute={VIEW_USER_ROUTE}
                      onActiveHandler={onActiveHandler}
                    />
                  </TableCell>
                </TableRow>
              );
            })}
          </Fragment>
        )}
      </TableComponent>

      <NoDataFound noData={noData} />
    </TableContainer>
  );
};

export default UsersTable;
